import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Plane,
  Sparkles,
  Shield,
  Clock,
  CheckCircle,
  ChevronRight,
  FileText,
  Phone,
  Users,
  Star,
  Droplets,
  Wind,
  Camera,
  ClipboardCheck,
  BadgeCheck,
  Zap,
  Calendar,
  MapPin,
  Award,
} from "lucide-react";

const AircraftDetailingPage = () => {
  // Core Services
  const coreServices = [
    {
      icon: Droplets,
      title: "Exterior Aircraft Detailing",
      description: "Military-precision exterior cleaning and protection for aircraft presentation excellence.",
      services: [
        "Complete wash & dry",
        "Brightwork polish",
        "De-bug treatment",
        "De-ice residue removal",
        "Ceramic coating (premium)",
        "Paint protection",
      ],
    },
    {
      icon: Sparkles,
      title: "Interior Detailing",
      description: "Executive-level cabin presentation meeting the highest standards of cleanliness and comfort.",
      services: [
        "Deep cabin cleaning",
        "Leather treatment & conditioning",
        "Carpet extraction",
        "Cockpit sanitation",
        "Odor removal",
        "Surface disinfection",
      ],
    },
    {
      icon: ClipboardCheck,
      title: "Readiness Services",
      description: "Mission-ready aircraft preparation ensuring operational readiness and presentation standards.",
      services: [
        "FOD inspection",
        "Photo documentation",
        "Turnaround cleaning",
        "Pre-flight presentation",
        "Post-maintenance cleanup",
        "Departure preparation",
      ],
    },
    {
      icon: Shield,
      title: "Government & Military",
      description: "Documentation-first services aligned with government aircraft and military transient requirements.",
      services: [
        "Documentation logs",
        "Inspection readiness",
        "Military-style service reports",
        "Rapid deployment detailing",
        "24-hour response capability",
        "Chain-of-custody documentation",
      ],
    },
  ];

  // Pricing Packages
  const pricingPackages = [
    {
      category: "Light Jet",
      examples: "Citation CJ, Phenom 100, HondaJet",
      exterior: "$600 – $900",
      interior: "$400 – $600",
      full: "$900 – $1,500",
      turnaround: "$350+",
    },
    {
      category: "Midsize Jet",
      examples: "Citation XLS, Learjet 60, Hawker 800",
      exterior: "$900 – $1,200",
      interior: "$600 – $800",
      full: "$1,500 – $2,000",
      turnaround: "$500+",
    },
    {
      category: "Large Cabin",
      examples: "Challenger 300, Gulfstream G280, Falcon 900",
      exterior: "$1,200 – $1,800",
      interior: "$800 – $1,200",
      full: "$2,000 – $3,000",
      turnaround: "$750+",
    },
    {
      category: "Heavy / Ultra-Long",
      examples: "Gulfstream G650, Global 7500, Falcon 8X",
      exterior: "$1,800 – $2,500",
      interior: "$1,200 – $1,500",
      full: "$3,000 – $3,500",
      turnaround: "$1,000+",
    },
    {
      category: "Airliner / VIP Transport",
      examples: "BBJ, ACJ, Military VIP",
      exterior: "$3,500+",
      interior: "$2,000+",
      full: "$5,000+",
      turnaround: "Quote",
    },
  ];

  // Target Clients
  const targetClients = [
    { icon: Plane, title: "Private Jet Operators", description: "Owner-operators and management companies" },
    { icon: Users, title: "Charter Fleets", description: "Part 135 operators and charter companies" },
    { icon: Shield, title: "Government Aircraft", description: "Federal, state, and municipal aviation" },
    { icon: Star, title: "Military Transient", description: "Transient military aircraft at civilian airports" },
    { icon: Award, title: "FBO Partners", description: "Fixed-base operators seeking detail services" },
    { icon: Zap, title: "Medevac & EMS", description: "Air ambulance and emergency medical services" },
  ];

  // Why AMR
  const differentiators = [
    "Veteran-led crews with military attention to detail",
    "Airport-based operations at New Orleans Lakefront (KNEW)",
    "24-hour rapid response capability",
    "Government-ready documentation and reporting",
    "FOD-awareness trained personnel",
    "Flexible scheduling including nights and weekends",
    "Monthly contract programs with priority scheduling",
  ];

  // Contract Options
  const contractOptions = [
    {
      title: "Per-Service",
      description: "Individual detail services scheduled on demand. Ideal for occasional needs.",
      features: ["No commitment required", "Flexible scheduling", "Full service menu access"],
    },
    {
      title: "Monthly Retainer",
      description: "Guaranteed availability and priority scheduling for regular operators.",
      features: ["Priority scheduling", "Discounted rates", "Dedicated crew assignment", "Monthly reporting"],
    },
    {
      title: "Fleet Contract",
      description: "Comprehensive detailing program for charter operators and flight departments.",
      features: ["Volume pricing", "Scheduled rotations", "Aircraft tracking", "Performance metrics"],
    },
    {
      title: "FBO Partnership",
      description: "White-label or co-branded detailing services for FBO operations.",
      features: ["On-site presence", "Revenue sharing options", "Brand integration", "Turnkey solution"],
    },
  ];

  return (
    <div className="min-h-screen" data-testid="aircraft-detailing-page">
      {/* HERO SECTION */}
      <section
        className="relative min-h-[85vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1766759465475-b5cf6466aee0?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="detailing-hero"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4 animate-fade-in-up">
              AIRCRAFT DETAILING & READINESS DIVISION
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
              MILITARY PRECISION.<br />
              <span className="text-[#c5a04a]">MISSION-READY AIRCRAFT.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-4 leading-relaxed animate-fade-in-up animation-delay-200">
              Professional aircraft detailing and readiness services for private jets, charter fleets, 
              government aircraft, and military transient operations. Veteran-powered crews delivering 
              military-grade presentation standards.
            </p>
            <p className="text-slate-300 mb-8 animate-fade-in-up animation-delay-200">
              Based at New Orleans Lakefront Airport (KNEW). 24-hour response capability.
            </p>
            
            {/* Service Highlights */}
            <div className="flex flex-wrap gap-6 mb-10 animate-fade-in-up animation-delay-300">
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#c5a04a]" />
                <span className="text-sm">Exterior & Interior Detailing</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#c5a04a]" />
                <span className="text-sm">Government-Ready Documentation</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#c5a04a]" />
                <span className="text-sm">24-Hour Response Available</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/contact?type=contracting">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="hero-quote-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Request Service Quote
                </Button>
              </Link>
              <a href="tel:504-319-7683">
                <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="hero-call-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  504-319-7683
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="py-20 bg-white" data-testid="core-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">CORE SERVICES</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {coreServices.map((service, index) => (
              <Card key={index} className="card-tactical h-full" data-testid={`service-${index}`}>
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-4">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{service.description}</p>
                  <ul className="grid grid-cols-2 gap-3">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">INVESTMENT</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">SERVICE PRICING</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Pricing varies by aircraft size, condition, and service requirements. 
              Monthly contracts and fleet programs include volume discounts.
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-sm shadow-sm" data-testid="pricing-table">
              <thead>
                <tr className="bg-[#1a365d] text-white">
                  <th className="p-4 text-left font-heading tracking-wider text-sm">AIRCRAFT CLASS</th>
                  <th className="p-4 text-center font-heading tracking-wider text-sm">EXTERIOR</th>
                  <th className="p-4 text-center font-heading tracking-wider text-sm">INTERIOR</th>
                  <th className="p-4 text-center font-heading tracking-wider text-sm">FULL DETAIL</th>
                  <th className="p-4 text-center font-heading tracking-wider text-sm">TURNAROUND</th>
                </tr>
              </thead>
              <tbody>
                {pricingPackages.map((pkg, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="p-4 border-b border-slate-200">
                      <p className="font-heading text-[#1a365d]">{pkg.category}</p>
                      <p className="text-xs text-slate-500">{pkg.examples}</p>
                    </td>
                    <td className="p-4 text-center border-b border-slate-200 text-slate-700">{pkg.exterior}</td>
                    <td className="p-4 text-center border-b border-slate-200 text-slate-700">{pkg.interior}</td>
                    <td className="p-4 text-center border-b border-slate-200 font-medium text-[#1a365d]">{pkg.full}</td>
                    <td className="p-4 text-center border-b border-slate-200 text-[#c5a04a] font-medium">{pkg.turnaround}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="text-center text-slate-500 text-sm mt-6">
            *Pricing subject to aircraft condition assessment. Premium coatings, heavy soiling, and expedited service may incur additional charges.
          </p>
        </div>
      </section>

      {/* TARGET CLIENTS */}
      <section className="py-20 bg-[#1a365d]" data-testid="target-clients">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CLIENTS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-white">WHO WE SERVE</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {targetClients.map((client, index) => (
              <div
                key={index}
                className="p-6 border border-slate-700 rounded-sm hover:border-[#c5a04a] transition-colors"
                data-testid={`client-${index}`}
              >
                <client.icon className="w-10 h-10 text-[#c5a04a] mb-4" />
                <h3 className="font-heading text-lg text-white mb-2">{client.title}</h3>
                <p className="text-slate-300 text-sm">{client.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY AMR */}
      <section className="py-20 bg-white" data-testid="why-amr">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">ADVANTAGE</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                WHY AMR GLOBAL
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                AMR Global Holdings LLC delivers military-precision aircraft detailing with 
                veteran-led crews trained in FOD awareness, documentation discipline, and 
                government-ready service standards. We're not a car wash company — we're 
                Aircraft Readiness Infrastructure.
              </p>
              <ul className="space-y-4">
                {differentiators.map((diff, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{diff}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#1a365d] p-8 rounded-sm">
                <Clock className="w-10 h-10 text-[#c5a04a] mb-4" />
                <p className="text-4xl font-heading text-white mb-2">24/7</p>
                <p className="text-slate-300 text-sm tracking-wider">RESPONSE AVAILABLE</p>
              </div>
              <div className="bg-[#c5a04a] p-8 rounded-sm">
                <Users className="w-10 h-10 text-[#1a365d] mb-4" />
                <p className="text-4xl font-heading text-[#1a365d] mb-2">100%</p>
                <p className="text-[#1a365d] text-sm tracking-wider">VETERAN CREWS</p>
              </div>
              <div className="col-span-2 bg-slate-100 p-8 rounded-sm">
                <MapPin className="w-10 h-10 text-[#1a365d] mb-4" />
                <p className="text-2xl font-heading text-[#1a365d] mb-2">KNEW AIRPORT</p>
                <p className="text-slate-600 text-sm">New Orleans Lakefront Airport base of operations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTRACT OPTIONS */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="contract-options">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">PROGRAMS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">CONTRACT OPTIONS</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contractOptions.map((option, index) => (
              <Card key={index} className="card-tactical h-full" data-testid={`contract-${index}`}>
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-xl text-[#1a365d]">{option.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm mb-6">{option.description}</p>
                  <ul className="space-y-2">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="py-20 bg-[#1a365d]" data-testid="service-area">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">COVERAGE</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">SERVICE AREA</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-[#c5a04a] font-heading text-sm mb-4">PRIMARY BASE</p>
              <p className="text-white font-heading text-xl">NEW ORLEANS LAKEFRONT (KNEW)</p>
              <p className="text-slate-300 text-sm mt-2">Home base operations</p>
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading text-sm mb-4">REGIONAL COVERAGE</p>
              <p className="text-white font-heading text-xl">GULF COAST AIRPORTS</p>
              <p className="text-slate-300 text-sm mt-2">MSY • Mobile • Houston • Florida</p>
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading text-sm mb-4">EXPANSION</p>
              <p className="text-white font-heading text-xl">NATIONAL DEPLOYMENT</p>
              <p className="text-slate-300 text-sm mt-2">Mobile teams available</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-white" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-16 h-16 text-[#c5a04a] mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-4">
            MISSION-READY AIRCRAFT PRESENTATION
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Contact AMR Global Holdings LLC to schedule aircraft detailing services 
            or discuss fleet contract programs.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link to="/contact?type=contracting">
              <Button className="btn-tactical btn-primary text-sm" data-testid="cta-quote-btn">
                <FileText className="w-4 h-4 mr-2" />
                Request Service Quote
              </Button>
            </Link>
            <a href="tel:504-319-7683">
              <Button variant="outline" className="btn-tactical btn-outline text-sm" data-testid="cta-call-btn">
                <Phone className="w-4 h-4 mr-2" />
                Call 504-319-7683
              </Button>
            </a>
          </div>
          <p className="text-slate-500 text-sm">
            Email: AMREID@AMRGLOBALHOLDINGS.COM | New Orleans Lakefront Airport (KNEW)
          </p>
        </div>
      </section>

      {/* FOOTER TAGLINE */}
      <section className="py-8 bg-slate-100" data-testid="tagline-footer">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#1a365d] font-heading tracking-wider text-sm">
            AMR GLOBAL HOLDINGS LLC — VETERAN POWERED. MISSION READY.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AircraftDetailingPage;
